import {
  completeOrgSelection,
  loginSuccess,
  setPendingOrgFinalize,
} from "../api/auth/authSlice";
import { fetchOrgRequest } from "../api/organizations/orgSlice";
import { fetchSubscriptionRequest } from "../api/subscription/subscriptionSlice";
import { fetchUserRequest } from "../api/user/userSlice";
import { authApiClient } from "./axios";
import {
  readAuthEmail,
  resolveSafePostAuthPath,
  setAuthEmail,
  setRequiresOrgSelection,
} from "./authSession";

export { resolveSafePostAuthPath };

export function persistAuthSession({
  requiresOrgSelection = false,
  email,
} = {}) {
  if (email) {
    setAuthEmail(email);
  }

  setRequiresOrgSelection(!!requiresOrgSelection);

  return { requiresOrgSelection: !!requiresOrgSelection };
}

export function resolvePostAuthRedirect(
  requiresOrgSelection,
  redirectTo = "/dashboard",
) {
  if (requiresOrgSelection) {
    return "/select-organization";
  }

  return resolveSafePostAuthPath(redirectTo);
}

export function applySelectedSession(dispatch, queryClient) {
  persistAuthSession({ requiresOrgSelection: false });
  dispatch(completeOrgSelection());

  if (queryClient) {
    queryClient.clear();
  }

  dispatch(fetchOrgRequest());
  dispatch(fetchSubscriptionRequest());
  dispatch(fetchUserRequest());
}

export async function selectOrganization(
  authApiClientInstance,
  organizationId,
) {
  const response = await authApiClientInstance.post(
    `/organizations/${organizationId}/token`,
    undefined,
    {
      skipErrorSnackbar: true,
    },
  );

  return response.data;
}

export async function finalizeOrganizationAccess({
  selectResponse,
  organizationId,
  organizationName,
  email,
  dispatch,
  queryClient,
  onMfaRequired,
  persistPendingMfa = false,
}) {
  if (selectResponse?.mfa_required) {
    if (persistPendingMfa) {
      dispatch?.(
        setPendingOrgFinalize({
          organizationId,
          organizationName,
          email: email || readAuthEmail(),
        }),
      );
    }
    onMfaRequired?.({
      organizationId,
      message: selectResponse.message,
    });

    return { completed: false, mfaRequired: true };
  }

  if (!selectResponse?.token) {
    throw new Error("Organization selection did not return a session token.");
  }

  applySelectedSession(dispatch, queryClient);

  return { completed: true, mfaRequired: false };
}

export async function completePostLoginFlow({
  requiresOrgSelection,
  email,
  dispatch,
  navigate,
  redirectTo = "/dashboard",
  queryClient,
  onMfaRequired,
}) {
  persistAuthSession({ requiresOrgSelection, email });

  if (requiresOrgSelection) {
    dispatch(loginSuccess({ requiresOrgSelection: true }));

    navigate("/select-organization", {
      replace: true,
      state: { redirectTo: resolveSafePostAuthPath(redirectTo), email },
    });

    return { mfaRequired: false };
  }

  dispatch(
    loginSuccess({ requiresOrgSelection: false, pendingOrgFinalize: true }),
  );

  try {
    const orgsResponse = await authApiClient.get("/organizations");
    const organizations = orgsResponse.data?.organizations ?? [];

    if (organizations.length !== 1) {
      throw new Error("Unable to access your organization. Please try again.");
    }

    const selectResponse = await selectOrganization(
      authApiClient,
      organizations[0].id,
    );
    const result = await finalizeOrganizationAccess({
      selectResponse,
      organizationId: organizations[0].id,
      organizationName: organizations[0].name,
      email,
      dispatch,
      queryClient,
      persistPendingMfa: true,
      onMfaRequired: (payload) =>
        onMfaRequired?.({
          ...payload,
          organizationName: organizations[0].name,
          email,
        }),
    });

    if (result.mfaRequired) {
      return {
        mfaRequired: true,
        organizationId: organizations[0].id,
        organizationName: organizations[0].name,
        email,
      };
    }

    navigate(resolveSafePostAuthPath(redirectTo), { replace: true });

    return { mfaRequired: false };
  } catch (err) {
    return {
      mfaRequired: false,
      error: err,
    };
  }
}
